"use client";

import * as TabsPrimitive from "@radix-ui/react-tabs";
import { motion } from "framer-motion";
import * as React from "react";

import { cn } from "../lib/utils";

const Tabs = TabsPrimitive.Root;

const TabsList = ({
	ref,
	className,
	...props
}: React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> & {
	ref?: React.RefObject<React.ElementRef<typeof TabsPrimitive.List>>;
}) => (
	<TabsPrimitive.List
		ref={ref}
		className={cn(
			"inline-flex h-10 items-center justify-center rounded-md bg-muted p-1 text-muted-foreground",
			className,
		)}
		{...props}
	/>
);
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = ({
	ref,
	className,
	variant = "default",
	children,
	...props
}: React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> & {
	ref?: React.RefObject<React.ElementRef<typeof TabsPrimitive.Trigger>>;
	variant?: "default" | "primary";
}) => {
	// Create a ref to store the element
	const triggerRef = React.useRef<HTMLButtonElement>(null);
	// Combine the forwarded ref with our local ref
	const combinedRef = useCombinedRefs(ref, triggerRef);
	// State to track if this tab is active
	const [isActive, setIsActive] = React.useState(false);

	const tabId = React.useRef<string>(Math.random().toString());

	// Effect to check data-state attribute changes
	React.useEffect(() => {
		if (!triggerRef.current) return;

		// Initial check
		setIsActive(triggerRef.current.getAttribute("data-state") === "active");

		// Set up mutation observer to watch for data-state changes
		const observer = new MutationObserver((mutations) => {
			for (const mutation of mutations) {
				if (
					mutation.type === "attributes" &&
					mutation.attributeName === "data-state"
				) {
					const newState = triggerRef.current?.getAttribute("data-state");
					setIsActive(newState === "active");
				}
			}
		});

		observer.observe(triggerRef.current, { attributes: true });

		return () => {
			observer.disconnect();
		};
	}, []);

	return (
		<TabsPrimitive.Trigger
			ref={combinedRef}
			className={cn(
				"relative inline-flex items-center justify-center whitespace-nowrap px-3 py-1.5 text-sm font-medium ring-offset-background transition-all duration-200 ease-in-out focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
				variant === "default" &&
					"data-[state=active]:text-foreground hover:bg-muted/50 hover:rounded-sm",
				variant === "primary" &&
					"data-[state=active]:text-primary-foreground hover:bg-muted/50 hover:rounded-sm",
				className,
			)}
			{...props}
		>
			<span className="relative z-10">{children}</span>
			{isActive && (
				<motion.span
					layoutId={`tab-trigger-${props.value}`}
					transition={{ type: "spring", duration: 0.4 }}
					className={cn(
						"absolute inset-0 z-0 rounded-sm shadow-sm",
						variant === "default" && "bg-background",
						variant === "primary" && "bg-primary",
					)}
				/>
			)}
		</TabsPrimitive.Trigger>
	);
};

// Helper function to combine refs
function useCombinedRefs<T>(
	...refs: Array<React.Ref<T> | undefined>
): React.RefCallback<T> {
	return React.useCallback(
		(element: T) => {
			for (const ref of refs) {
				if (!ref) continue;

				if (typeof ref === "function") {
					ref(element);
				} else {
					(ref as React.MutableRefObject<T>).current = element;
				}
			}
		},
		[refs],
	);
}
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = ({
	ref,
	className,
	...props
}: React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content> & {
	ref?: React.RefObject<React.ElementRef<typeof TabsPrimitive.Content>>;
}) => (
	<TabsPrimitive.Content
		ref={ref}
		className={cn(
			"mt-2 ring-offset-background focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
			className,
		)}
		{...props}
	/>
);
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsList, TabsTrigger, TabsContent };
